/**
 * `/accounts/:id` — the account 360, backed by the single
 * `GET /api/accounts/:id/summary`.
 *
 * **Roll-up figures are computed across every opportunity on the account,
 * regardless of the viewer's edit scope, and on the net basis** (§2, §6.11).
 * Scoping governs write affordances, not totals: an account total that quietly
 * excluded a colleague's deals would be wrong on every screen showing it. When
 * the account carries both direct and channel deals the mix is stated, because
 * no total may span two price bases without saying so.
 *
 * **Won backlog is reported separately** from open pipeline (§6.2): won but not
 * yet in production is neither pipeline nor revenue, and folding it into either
 * is how a forecast becomes fiction.
 */

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import type { EntityContext } from '@menlo/shared';
import { useApiQuery, type Paged, type Row } from '@/lib/queries';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TabList, TabPanel, useTab, type TabDefinition } from '@/components/ui/tabs';
import { NotesPanel } from '@/features/notes/NotesPanel';
import { useNoteComposer } from '@/features/notes/useNoteComposer';
import { AttachmentsPanel } from '@/features/attachments/AttachmentsPanel';
import { ActionItemRow } from '@/features/actions/ActionItems';
import { ContactsPanel } from '@/features/pickers/ContactsPanel';
import { MeetingDialog } from '@/features/meetings/MeetingDialog';
import { CreateProgramDialog } from '@/features/programs/CreateProgramDialog';
import { EditAccountDialog } from '@/features/accounts/EditAccountDialog';
import { StatusBadge } from '@/components/domain/Chips';
import { MoneyCell } from '@/components/domain/Money';
import { useStages } from '@/lib/reference';
import {
  daysSince,
  formatDate,
  formatDateTime,
  formatMoney,
  humanize,
  sumMoney,
} from '@/lib/format';
import { cn } from '@/lib/utils';

export function AccountDetail(): React.ReactElement {
  const { id = '' } = useParams();
  const { byCode } = useStages();
  const [loggingMeeting, setLoggingMeeting] = useState(false);
  const [creatingProgram, setCreatingProgram] = useState(false);
  const [editing, setEditing] = useState(false);
  // This panel is on a tab too, so its composer outlives the panel for the
  // same reason the opportunity's does: a draft must not die on a tab switch.
  const noteComposer = useNoteComposer();

  const [includeSites, setIncludeSites] = useState(true);

  const context = useApiQuery<EntityContext>(
    ['account-summary', id],
    `/api/accounts/${id}/summary`,
  );
  const record = (context.data?.record ?? {}) as Row;
  const canWrite = context.data?.canWrite === true;

  /**
   * Sites — the live direct children, for the strip of links, from the
   * summary. Not from a page of the account list: that is capped at 200, and
   * once dev passed 200 accounts a parent's sites fell off it (session 6).
   */
  const children = useMemo(() => context.data?.sites ?? [], [context.data]);

  /**
   * Session 6: **the parent rolls up its sites** — every deal, contact and
   * total of the account and all its descendants comes back from the summary
   * itself, each row labelled with its site (`account_name`). The toggle
   * narrows the page to this account's own rows; it is on by default because
   * the roll-up is the ruling ("Companies with multiple locations").
   */
  const rolledUp = includeSites || children.length === 0;
  const opportunities = useMemo(
    () =>
      ((context.data?.opportunities ?? []) as unknown as Row[]).filter(
        (row) => rolledUp || row['account_id'] === id,
      ),
    [context.data, rolledUp, id],
  );
  const siteContacts = useMemo(
    () =>
      rolledUp
        ? (context.data?.people ?? []).filter(
            (person) => person.account_id !== undefined && person.account_id !== id,
          )
        : [],
    [context.data, rolledUp, id],
  );

  const programs = useApiQuery<Paged<Row>>(
    ['programs', id],
    `/api/programs?accountId=${id}&pageSize=100`,
  );
  const meetings = useApiQuery<Paged<Row>>(
    ['meetings', id],
    `/api/meetings?accountId=${id}&pageSize=10&sort=-starts_at`,
  );

  const rollup = useMemo(() => {
    const open = opportunities.filter((row) => row['status'] === 'open');
    const won = opportunities.filter((row) => row['status'] === 'won');
    const productionOrder = byCode.get('PRODUCTION')?.sort_order ?? 80;
    const backlog = won.filter((row) => Number(row['stage_sort_order']) < productionOrder);

    const bases = new Set(opportunities.map((row) => String(row['price_basis'])));

    return {
      openCount: open.length,
      openNet: sumMoney(open.map((row) => row['annual_revenue_net'] as string)),
      weighted: sumMoney(open.map((row) => row['weighted_revenue'] as string)),
      wonCount: won.length,
      wonNet: sumMoney(won.map((row) => row['annual_revenue_net'] as string)),
      backlogCount: backlog.length,
      backlogNet: sumMoney(backlog.map((row) => row['annual_revenue_net'] as string)),
      mixed: bases.size > 1,
      bases: [...bases],
    };
  }, [opportunities, byCode]);

  const lastContact = useMemo(() => {
    const stamps = opportunities
      .map((row) => row['last_customer_contact_at'])
      .filter((value): value is string => typeof value === 'string');
    return stamps.sort().at(-1) ?? null;
  }, [opportunities]);

  const tabs: TabDefinition[] = [
    { id: 'overview', label: 'Overview' },
    {
      id: 'contacts',
      label: 'Contacts',
      count:
        (context.data?.people ?? []).filter((person) => (person.account_id ?? id) === id).length +
        siteContacts.length,
    },
    { id: 'activity', label: 'Activity' },
    { id: 'files', label: 'Files', count: context.data?.attachments.length },
  ];
  const [tab, setTab] = useTab(tabs);

  if (context.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (context.error !== null) {
    return (
      <p className="rounded-md border border-destructive/40 bg-red-50 p-3 text-sm text-red-900">
        {context.error.message}
      </p>
    );
  }

  const staleContact = lastContact === null || (daysSince(lastContact) ?? 0) > 30;
  const ndaDays = daysSince(record['nda_expiry_date'] as string | null);

  /**
   * RF-0003. `website` and `hq_country` have existed since Phase 4 and were
   * rendered nowhere; the address is new. One line, in postal order, with the
   * ISO country last — the parts are stored separately so a duplicate check
   * can compare them, but a reader wants the address.
   */
  const website = typeof record['website'] === 'string' ? record['website'] : null;
  const address = (
    ['address_line1', 'address_line2', 'city', 'state_region', 'postal_code', 'hq_country'] as const
  )
    .map((column) => record[column])
    .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
    .join(', ');

  return (
    <div className="space-y-4">
      <header className="space-y-3 rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            {record['parent_account_name'] !== null &&
              record['parent_account_name'] !== undefined && (
                <p className="text-xs text-muted-foreground">
                  <Link
                    to={`/accounts/${String(record['parent_account_id'])}`}
                    className="hover:underline"
                  >
                    {String(record['parent_account_name'])}
                  </Link>{' '}
                  /
                </p>
              )}
            <h1 className="text-lg font-semibold">{String(record['name'] ?? '')}</h1>
            <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="outline">{humanize(String(record['segment']))}</Badge>
              <Badge variant="outline">{String(record['region'])}</Badge>
              <Badge variant="outline">
                {humanize(String(record['channel_type']))}
                {record['rep_firm_name'] !== null && record['rep_firm_name'] !== undefined
                  ? ` — ${String(record['rep_firm_name'])}`
                  : ''}
              </Badge>
              <span>Owner {String(record['owner_name'] ?? '—')}</span>
              <StatusBadge status={String(record['status'])} />
            </p>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {website === null ? (
                <span>No website recorded</span>
              ) : (
                // §9: never dereferenced by the server, and rel'd on the way out.
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  {website}
                </a>
              )}
              <span>{address === '' ? 'No address recorded' : address}</span>
            </p>
          </div>

          <div className="text-right">
            <p
              className={cn(
                'text-sm font-medium',
                staleContact ? 'text-red-700' : 'text-emerald-700',
              )}
            >
              Last contact:{' '}
              {lastContact === null ? 'never' : `${String(daysSince(lastContact))} days ago`}
            </p>
            <p className="text-xs text-muted-foreground">
              From meetings with a customer attendee — never a stored column.
            </p>
            {canWrite && (
              <div className="mt-2 flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                  Edit account
                </Button>
                <Button size="sm" variant="outline" onClick={() => setLoggingMeeting(true)}>
                  Log a meeting
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Compliance strip */}
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-muted/40 p-2 text-xs">
          <span className="font-medium">NDA</span>
          {record['nda_on_file'] === true ? (
            <Badge
              variant={
                ndaDays !== null && ndaDays > 0
                  ? 'danger'
                  : ndaDays !== null && ndaDays > -60
                    ? 'risk'
                    : 'success'
              }
            >
              {ndaDays !== null && ndaDays > 0
                ? `Expired ${formatDate(record['nda_expiry_date'] as string)}`
                : `On file to ${formatDate(record['nda_expiry_date'] as string)}`}
            </Badge>
          ) : (
            <Badge variant="risk">Not on file</Badge>
          )}

          {record['export_sensitive'] === true && (
            <Badge variant="risk">
              <ShieldAlert className="h-3 w-3" aria-hidden="true" />
              Export-sensitive — new opportunities default the attestation to &ldquo;Yes&rdquo;
            </Badge>
          )}
        </div>
      </header>

      {children.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-2 text-xs">
          <span className="font-medium">
            {children.length} site{children.length === 1 ? '' : 's'}
          </span>
          <span className="flex flex-wrap gap-1">
            {children.map((child) => (
              <Link
                key={String(child['id'])}
                to={`/accounts/${String(child['id'])}`}
                className="rounded-full border px-2 py-0.5 hover:bg-accent"
              >
                {String(child['name'])}
              </Link>
            ))}
          </span>
          <label className="ml-auto flex items-center gap-1.5">
            <input
              type="checkbox"
              className="h-3.5 w-3.5"
              checked={includeSites}
              onChange={(event) => setIncludeSites(event.target.checked)}
            />
            Include its sites (and theirs) in the deals, contacts and figures below
          </label>
        </div>
      )}

      <section className="grid gap-3 sm:grid-cols-4">
        <Metric
          label="Open pipeline (net)"
          value={formatMoney(rollup.openNet)}
          hint={`${rollup.openCount} open · ${formatMoney(rollup.weighted)} weighted`}
        />
        <Metric
          label="Won business (net)"
          value={formatMoney(rollup.wonNet)}
          hint={`${rollup.wonCount} won, all stages`}
        />
        <Metric
          label="Won backlog, gross"
          value={formatMoney(rollup.backlogNet)}
          hint={`${rollup.backlogCount} won but not yet in production — reported separately from pipeline`}
        />
        <Metric
          label="Programs"
          value={String(programs.data?.total ?? 0)}
          hint={
            rolledUp && children.length > 0
              ? 'This account only — sites have their own programs'
              : 'Customer end programs, the unit of value in this funnel'
          }
        />
      </section>

      {rollup.mixed && (
        <p className="rounded-md border border-sky-600/40 bg-sky-50 p-2 text-xs text-sky-950">
          This account has both direct and channel deals ({rollup.bases.map(humanize).join(' and ')}
          ). The totals above are all on the Menlo net basis, so they are comparable; a
          customer-basis total across the same deals would be a larger, different number.
        </p>
      )}

      <TabList tabs={tabs} active={tab} onSelect={setTab} />

      <TabPanel id="overview" active={tab}>
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel title="Opportunities">
            <ul className="divide-y">
              {opportunities.map((row) => (
                <li
                  key={String(row['id'])}
                  className="flex items-center justify-between gap-2 py-1.5 text-sm"
                >
                  <span className="min-w-0">
                    <Link
                      to={`/opportunities/${String(row['id'])}`}
                      className="block truncate font-medium hover:underline"
                    >
                      {String(row['name'])}
                    </Link>
                    <span className="text-xs text-muted-foreground">
                      {String(row['stage_name'])} · {humanize(String(row['status']))}
                      {row['account_id'] !== id && (
                        <>
                          {' · '}
                          <Link
                            to={`/accounts/${String(row['account_id'])}`}
                            className="hover:underline"
                          >
                            {String(row['account_name'])}
                          </Link>
                        </>
                      )}
                    </span>
                  </span>
                  <MoneyCell value={row['annual_revenue_net'] as string} />
                </li>
              ))}
              {opportunities.length === 0 && (
                <li className="py-3 text-xs text-muted-foreground">No opportunities yet.</li>
              )}
            </ul>
          </Panel>

          <Panel title="Open actions across the account">
            <div>
              {(context.data?.openItems ?? []).map((item) => (
                <ActionItemRow key={item.id} item={item as unknown as Row} canWrite={canWrite} />
              ))}
              {(context.data?.openItems ?? []).length === 0 && (
                <p className="py-3 text-xs text-muted-foreground">Nothing open.</p>
              )}
            </div>
          </Panel>

          {/*
            The deliberate place to create a program, as against the stage
            gate, where a rep is merely told they need one. Gated on the
            account's own `canWrite`, which is the right question here:
            `ANCESTRY` resolves a program to its account, so this is the same
            answer the server will give. `ContactsPanel` on the next tab is
            gated the same way.
          */}
          <Panel
            title="Programs"
            action={
              canWrite && (
                <Button size="sm" variant="outline" onClick={() => setCreatingProgram(true)}>
                  New program
                </Button>
              )
            }
          >
            <ul className="divide-y">
              {(programs.data?.items ?? []).map((program) => (
                <li key={String(program['id'])} className="py-1.5 text-sm">
                  <Link
                    to={`/programs/${String(program['id'])}`}
                    className="font-medium hover:underline"
                  >
                    {String(program['name'])}
                  </Link>
                  <p className="text-xs text-muted-foreground">
                    {humanize(String(program['end_market']))} ·{' '}
                    {humanize(String(program['status']))}
                    {program['expected_sop_date'] !== null &&
                      ` · SOP ${formatDate(program['expected_sop_date'] as string)}`}
                  </p>
                </li>
              ))}
              {(programs.data?.items ?? []).length === 0 && (
                <li className="py-3 text-xs text-muted-foreground">No programs recorded.</li>
              )}
            </ul>
          </Panel>

          <Panel title="Recent meetings">
            <ul className="divide-y">
              {(meetings.data?.items ?? []).map((meeting) => (
                <li key={String(meeting['id'])} className="py-1.5 text-sm">
                  <p className="font-medium">{String(meeting['subject'])}</p>
                  <p className="text-xs text-muted-foreground">
                    {humanize(String(meeting['meeting_type']))} ·{' '}
                    {formatDateTime(meeting['starts_at'] as string)}
                  </p>
                </li>
              ))}
              {(meetings.data?.items ?? []).length === 0 && (
                <li className="py-3 text-xs text-muted-foreground">Nothing logged yet.</li>
              )}
            </ul>
          </Panel>
        </div>
      </TabPanel>

      <TabPanel id="contacts" active={tab}>
        <div className="space-y-4">
          <ContactsPanel accountId={id} canWrite={canWrite} />
          {siteContacts.length > 0 && (
            <Panel title="Contacts at its sites">
              <ul className="divide-y">
                {siteContacts.map((person) => (
                  <li
                    key={person.contact_id}
                    className="flex items-center justify-between gap-2 py-1.5 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">
                        {person.first_name} {person.last_name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {person.title ?? humanize(person.buying_role)}
                      </span>
                    </span>
                    <Link
                      to={`/accounts/${String(person.account_id)}`}
                      className="shrink-0 text-xs hover:underline"
                    >
                      {person.account_name}
                    </Link>
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </div>
      </TabPanel>

      <TabPanel id="activity" active={tab}>
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel title="Timeline">
            <ol className="space-y-1.5 text-sm">
              {(context.data?.timeline ?? []).map((entry, index) => (
                <li key={index} className="flex gap-2">
                  <span className="w-36 shrink-0 text-xs text-muted-foreground">
                    {formatDateTime(entry.at)}
                  </span>
                  <span>{entry.summary}</span>
                </li>
              ))}
            </ol>
          </Panel>
          <Panel title="Notes">
            <NotesPanel
              entityType="account"
              entityId={id}
              canWrite={canWrite}
              composer={noteComposer}
              compact
            />
          </Panel>
        </div>
      </TabPanel>

      <TabPanel id="files" active={tab}>
        <AttachmentsPanel entityType="account" entityId={id} canWrite={canWrite} />
      </TabPanel>

      {editing && (
        <EditAccountDialog
          account={record}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void context.refetch();
          }}
        />
      )}

      {creatingProgram && (
        <CreateProgramDialog
          accountId={id}
          onClose={() => setCreatingProgram(false)}
          onCreated={() => setCreatingProgram(false)}
        />
      )}

      {loggingMeeting && (
        <MeetingDialog
          accountId={id}
          onClose={() => setLoggingMeeting(false)}
          onSaved={() => {
            setLoggingMeeting(false);
            void context.refetch();
            void meetings.refetch();
          }}
        />
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}): React.ReactElement {
  return (
    <div className="rounded-lg border bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold tabular-nums">{value}</p>
      <p className="text-[11px] text-muted-foreground">{hint}</p>
    </div>
  );
}

function Panel({
  title,
  action,
  children,
}: {
  title: string;
  /** A primary action for this panel, shown on the heading row. */
  action?: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="rounded-lg border bg-card p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-sm font-medium">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}
